import type { StoryCtx } from './ctx.js';
import type { FieldValue, NodeType, StoryNode } from './types.js';

// ---------------------------------------------------------------------------
// Automatische Bilder: Ein Objekt ohne eigenes Titelbild und ohne Standardbild seiner Klasse bekommt ein erzeugtes Bild nach der
// Vorlage seiner Klasse: ein im Browser gezeichnetes Motiv vor einem Farbverlauf in einer blassen Farbe (Porträt, Gruppe, Gebäude,
// Landschaft, Gegenstand, Grundformen; eine Szene ein Blatt mit Textzeilen; web lib/autoAvatars.ts). Farben und Variante ergeben sich
// aus dem Farbplatz; Szenen tragen die Farbe ihres Kapitels (alle Szenen eines Kapitels gleich, ohne Kapitel neutral). Jedes Element und jedes Kapitel bekommt beim Anlegen einen Farbplatz (fortlaufend je Klasse,
// `colorSlot`, der kleinste freie); die Farbtabelle ist so geordnet, dass jede nächste Farbe möglichst weit von allen vorigen liegt – so kommt eine ähnliche
// Farbe in einer Klasse möglichst spät. Ist die Tabelle ausgereizt, beginnt sie mit verschobenem Farbton von vorn. Ohne Farbplatz
// (ältere Daten vor dem ersten Öffnen) leitet sich der Platz aus der ID ab. Erzeugt wird das Bild im Browser; hier entsteht nur seine
// Kennung (`auto:<vorlage>:<farbplatz>`).
// ---------------------------------------------------------------------------

export const AUTO_TEMPLATES = ['person', 'location', 'landscape', 'organisation', 'objects', 'concept', 'scene'] as const;
export type AutoTemplate = (typeof AUTO_TEMPLATES)[number];

type Hsv = { h: number; s: number; v: number };

/** HSV (Grad, 0–1, 0–1) → RGB (0–1) */
function rgbOf({ h, s, v }: Hsv): [number, number, number] {
  const k = (n: number): number => (n + h / 60) % 6;
  const c = (n: number): number => v - v * s * Math.max(0, Math.min(k(n), 4 - k(n), 1));
  return [c(5), c(3), c(1)];
}

const hex = (hsv: Hsv): string => `#${rgbOf(hsv).map((x) => Math.round(255 * x).toString(16).padStart(2, '0')).join('')}`;

/** RGB → OKLab (wahrnehmungsgleicher Farbraum: gleiche Abstände sehen gleich verschieden aus) */
function oklab([r, g, b]: [number, number, number]): [number, number, number] {
  const lin = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const [R, G, B] = [lin(r), lin(g), lin(b)];
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}

/**
 * Die Farbtabelle: schwache, helle Farben über alle Farbtöne (HSV: Sättigung 20–30 %, Value 85–91 % – blasse Pastelltöne, nichts
 * Grelles; Ring und Motiv der Vorlagen stehen darauf dunkel). Aus allen Kandidaten wird reihum die genommen, die von den schon
 * genommenen im OKLab-Raum am weitesten entfernt ist (die erste: ein helles Ocker).
 */
function buildTable(size: number): Hsv[] {
  const candidates: Hsv[] = [];
  for (let h = 0; h < 360; h += 5) for (const s of [0.2, 0.25, 0.3]) for (const v of [0.85, 0.88, 0.91]) candidates.push({ h, s, v });
  const lab = candidates.map((c) => oklab(rgbOf(c)));
  const dist = (a: number[], b: number[]): number => Math.hypot(a[0]! - b[0]!, a[1]! - b[1]!, a[2]! - b[2]!);
  const first = candidates.findIndex((c) => c.h === 35 && c.s === 0.25 && c.v === 0.88);
  const picked = [first];
  const nearest = lab.map((p) => dist(p, lab[first]!));
  while (picked.length < size) {
    let best = -1;
    for (let i = 0; i < candidates.length; i++) if (!picked.includes(i) && (best < 0 || nearest[i]! > nearest[best]!)) best = i;
    picked.push(best);
    for (let i = 0; i < candidates.length; i++) nearest[i] = Math.min(nearest[i]!, dist(lab[i]!, lab[best]!));
  }
  return picked.map((i) => candidates[i]!);
}

const TABLE = buildTable(24);

/** Die Farbtabelle in ihrer Reihenfolge (Farbplatz 0, 1, 2 …) */
export const AUTO_COLORS: readonly string[] = TABLE.map(hex);

/**
 * Farbe eines Farbplatzes: die Tabelle der Reihe nach; ist sie ausgereizt, beginnt sie von vorn mit verschobenem Farbton – in jeder
 * Runde um einen anderen Bruchteil (goldener Schnitt) von 15°, sodass die neuen Töne zwischen die bisherigen fallen.
 */
export function autoColorOf(slot: number): string {
  const n = Math.max(0, Math.floor(slot));
  const round = Math.floor(n / TABLE.length);
  const base = TABLE[n % TABLE.length]!;
  if (round === 0) return hex(base);
  const shift = ((round * 0.6180339887) % 1) * 15;
  return hex({ ...base, h: (base.h + shift) % 360 });
}

/** Hauttöne der Porträts, von hell bis dunkel */
const SKIN = ['#f3d2bd', '#ecc09f', '#deb08a', '#c99470', '#a8754f', '#8a5a3c', '#6b4430'];

/** Farbe zu Schwarz hin abgedunkelt (t: 0 unverändert, 1 schwarz) */
const darker = (color: string, t: number): string =>
  `#${[1, 3, 5].map((i) => Math.round(parseInt(color.slice(i, i + 2), 16) * (1 - t)).toString(16).padStart(2, '0')).join('')}`;

export interface AutoAvatar {
  /** Farbverlauf des Hintergrunds, oben und unten */
  top: string;
  bottom: string;
  skin: string;
  cloth: string;
}

/**
 * Farben des Porträts eines Farbplatzes: Hintergrund die Farbe des Platzes (nach unten dunkler), Kleidung eine andere Farbe der Tabelle,
 * kräftig abgedunkelt, der Hautton reihum (Nachbarplätze bekommen verschiedene).
 */
export function autoAvatarOf(slot: number): AutoAvatar {
  const n = Math.max(0, Math.floor(slot));
  const top = autoColorOf(n);
  return { top, bottom: darker(top, 0.28), skin: SKIN[(n * 3) % SKIN.length]!, cloth: darker(autoColorOf(n + 7), 0.55) };
}

/** Farbe des Kapitels einer Szene (sein Farbplatz, sonst aus seiner ID) – die ihres automatischen Bilds; null ohne Kapitel */
export function chapterColorOf(ctx: StoryCtx, scene: StoryNode): string | null {
  const chapter = scene.chapterId ? ctx.nodes.get(scene.chapterId) : undefined;
  return chapter ? autoColorOf(chapter.colorSlot ?? autoColorIndex(chapter.id)) : null;
}

/** Farbplatz aus einer ID (FNV-1a), für Objekte ohne vergebenen Platz */
export function autoColorIndex(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 0x01000193);
  return (h >>> 0) % TABLE.length;
}

/**
 * Farbplatz für ein neues Objekt der Klasse: der kleinste, den gerade keines hat (`taken`: in derselben Änderung schon vergebene) –
 * die Tabelle ist nach Abstand geordnet, ein frei gewordener Platz ist also die Farbe, die sich am stärksten von den vorhandenen abhebt.
 */
export function nextColorSlot(ctx: StoryCtx, typeId: string, taken: ReadonlySet<number> = new Set()): number {
  const used = new Set(taken);
  for (const e of ctx.story.entities) if (e.typeId === typeId && e.colorSlot !== undefined) used.add(e.colorSlot);
  for (const n of ctx.story.nodes) if (n.typeId === typeId && !n.entityId && n.colorSlot !== undefined) used.add(n.colorSlot);
  let slot = 0;
  while (used.has(slot)) slot++;
  return slot;
}

/** Vorlagen der vordefinierten Klassen */
const BUILTIN: Record<string, AutoTemplate> = { Person: 'person', Ort: 'location', Landschaft: 'landscape', Organisation: 'organisation', Gegenstand: 'objects', Konzept: 'concept' };

/** Vorlage einer Klasse: Szenen, die vordefinierten Elementklassen; eigene Ortsklassen eine Landschaft, eigene Elementklassen ein Gegenstand. */
export function autoTemplateOf(type: Pick<NodeType, 'name' | 'category' | 'builtin'>): AutoTemplate | null {
  if (type.category === 'scene') return 'scene';
  if (type.builtin && BUILTIN[type.name]) return BUILTIN[type.name]!;
  if (type.category === 'place') return 'landscape';
  if (type.category === 'object') return 'objects';
  return null;
}

const AUTO = /^auto:([a-z]+):(\d{1,6}|none)$/;

/** Farbe ohne Farbplatz (eine Szene ohne Kapitel): ein blasses, warmes Grau */
export const AUTO_NEUTRAL = '#d9d3ca';

/** Kennung eines automatischen Bilds (Farbplatz null: neutral) */
export const autoImageId = (template: AutoTemplate, slot: number | null): string => `auto:${template}:${slot ?? 'none'}`;

/** Vorlage, Farbplatz (null: neutral) und Farbe einer Kennung, sonst null (ein hochgeladenes Bild) */
export function parseAutoImageId(id: string): { template: AutoTemplate; slot: number | null; color: string } | null {
  const m = AUTO.exec(id);
  if (!m || !(AUTO_TEMPLATES as readonly string[]).includes(m[1]!)) return null;
  const slot = m[2] === 'none' ? null : Number(m[2]);
  return { template: m[1] as AutoTemplate, slot, color: slot === null ? AUTO_NEUTRAL : autoColorOf(slot) };
}

/**
 * Bild eines Objekts: das eigene Titelbild, sonst das Standardbild der Klasse, sonst das automatische (die Vorlage der Klasse in der
 * Farbe seines Farbplatzes – bei Kopien der des Elements, bei Szenen der ihres Kapitels; ohne Platz aus `seed`, der ID; `slot` null:
 * neutral). null, wenn die Klasse keine Vorlage hat (Metadaten, Grundlagen, Zeitlinien …).
 */
export function imageIdOf(
  type: Pick<NodeType, 'name' | 'category' | 'builtin' | 'imageKey' | 'placeholderId'>,
  fields: Record<string, FieldValue>,
  slot: number | null | undefined,
  seed: string,
): string | null {
  const own = type.imageKey ? fields[type.imageKey] : null;
  if (typeof own === 'string' && own !== '') return own;
  if (type.placeholderId) return type.placeholderId;
  const template = autoTemplateOf(type);
  return template ? autoImageId(template, slot === undefined ? autoColorIndex(seed) : slot) : null;
}
